import React from "react";
import { useNavigate } from "react-router-dom";
import { ICourseFlat } from "../domain/models/Course";
import { Purpose } from "../domain/enums/Purpose";
import { useBreakpoint } from "../custom-hooks/useBreakpoint";
import ImagePlaceholderSVG from "./shared/svg/ImagePlaceholder";
import { FILES_URL } from "../Config";
import { useTranslation } from "react-i18next";
import Button from "./ui/Button";
import { ButtonVariant } from "../types/constants";

interface CourseCardProps {
    course: ICourseFlat;
    showDate?: boolean;
    showButton?: boolean;
    showDetails?: boolean;
    fullWidth?: boolean;
}

export default function CourseCard({
    course,
    showDate = true,
    showButton = false,
    showDetails = false,
    fullWidth = false,
}: CourseCardProps) {
    const navigate = useNavigate();
    const { isTablet, isMobile } = useBreakpoint();
    const { t } = useTranslation();

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigate(`/courses/${course.id}`);
    };

    const getImageUrl = () => {
        if (course.imageUrl) return course.imageUrl;
        if (course.files && course.files.length > 0) {
            const coverFile = course.files.find((file) => file.purposeType === Purpose.COVER);
            if (coverFile?.filePath) {
                return `${FILES_URL}/${coverFile.filePath}`;
            }
        }
        return null;
    };

    return (
        <div
            className={`flex flex-col ${fullWidth ? "w-full" : isMobile ? "w-full" : isTablet ? "w-[336.5px] mx-auto" : "w-[305px]"}`}
        >
            <div
                className="w-full h-[250px] flex-shrink-0 mb-[16px] cursor-pointer hover:opacity-90 transition-opacity"
                onClick={handleClick}
            >
                {getImageUrl() ? (
                    <img src={getImageUrl()} alt={course.title} className="w-full h-full object-cover rounded-[6px]" />
                ) : (
                    <div className="w-full h-full bg-gray-100 rounded-[6px] flex items-center justify-center">
                        <ImagePlaceholderSVG fill="#bec6d5" classname="w-20 h-20" />
                    </div>
                )}
            </div>
            <div className="flex flex-col text-left gap-[8px] mb-[16px] flex-grow">
                <h3
                    className="font-medium text-lg uppercase text-text-700 line-clamp-3 leading-[120%] cursor-pointer hover:opacity-90"
                    onClick={handleClick}
                >
                    {course.title}
                </h3>
                <p className="font-normal text-base text-text-300 line-clamp-3 leading-[130%] h-[62.4px]">
                    {course.shortContent}
                </p>
                {showDetails && (
                    <div className="flex flex-col gap-[4px] mt-[8px]">
                        {course.author && (
                            <p className="text-sm leading-[140%]" style={{ color: "#404040" }}>
                                {course.author}
                            </p>
                        )}
                        {course.duration && (
                            <p className="text-sm leading-[140%]" style={{ color: "#404040" }}>
                                {course.duration}
                            </p>
                        )}
                        {course.topic && (
                            <p className="text-sm leading-[140%]" style={{ color: "#404040" }}>
                                {course.topic}
                            </p>
                        )}
                    </div>
                )}
            </div>
            {showButton && (
                <Button
                    variant={ButtonVariant.PRIMARY}
                    text={t("courses.goToCourse")}
                    onClick={handleClick}
                    className="!w-full mt-auto"
                />
            )}
        </div>
    );
}
